import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import apiFeedbacks, { FeedbackStatus, IFeedback } from 'api/feedbacks';
import translations from 'services/translations/translations';

export const FEEDBACK_STATUSES: FeedbackStatus[] = [
  'not-done',
  'in-progress',
  'done',
];

/**
 * How the development has reacted to a feedback: a text and a status. Saved when the text
 * is left or a status is chosen; a second click on the status takes it away.
 */
const FeedbackResponse = ({
  canRespond,
  feedback,
  onSaved,
}: {
  canRespond: boolean;
  feedback: IFeedback;
  onSaved: (feedback: IFeedback) => void;
}): JSX.Element => {
  const { t, i18n } = useTranslation([translations.common]);
  const tr = (key: string) => t(`${translations.common}:feedback.${key}`);
  const [response, setResponse] = useState(feedback.response ?? '');
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);

  const save = async (status: FeedbackStatus | null) => {
    setSaving(true);
    setFailed(false);
    try {
      await apiFeedbacks.respond(feedback.id, {
        response: response.trim(),
        status,
      });
      onSaved({
        ...feedback,
        respondedAt: new Date().toISOString(),
        response: response.trim() || null,
        status,
      });
    } catch {
      setFailed(true);
    } finally {
      setSaving(false);
    }
  };

  const responded =
    feedback.respondedAt === null
      ? null
      : `${feedback.respondedUserName ?? ''} ${new Date(
          feedback.respondedAt
        ).toLocaleDateString(i18n.language)}`.trim();

  return (
    <div className="feedback-response">
      {canRespond ? (
        <textarea
          className="form-control"
          maxLength={4000}
          onBlur={() => {
            if (response.trim() !== (feedback.response ?? '')) {
              save(feedback.status);
            }
          }}
          onChange={(event) => setResponse(event.target.value)}
          placeholder={tr('Response placeholder')}
          rows={2}
          value={response}
        />
      ) : (
        <p className="feedback-response-text">{feedback.response || '–'}</p>
      )}
      <div className="feedback-statuses">
        {FEEDBACK_STATUSES.map((status) => (
          <button
            aria-pressed={feedback.status === status}
            className={`feedback-status ${status} ${
              feedback.status === status ? 'chosen' : ''
            }`}
            disabled={!canRespond || saving}
            key={status}
            onClick={() => save(feedback.status === status ? null : status)}
            type="button"
          >
            {tr(`statuses.${status}`)}
          </button>
        ))}
      </div>
      {responded ? <p className="feedback-responded">{responded}</p> : null}
      {failed ? <p className="feedback-failed">{tr('Save failed')}</p> : null}
    </div>
  );
};

export default FeedbackResponse;
